import { Skeleton } from "../ui/Skeleton";

interface DetailPageSkeletonProps {
  /** Announced to screen readers, e.g. "Loading contract". */
  label: string;
  /** Summary cards under the header; 0 leaves them out. */
  cards?: number;
  /** Tab-strip pills; 0 leaves the strip out. */
  tabs?: number;
  /** Lines of body text in the content block. */
  lines?: number;
  /** Centres a title above the body, the way a document reads. */
  titledBody?: boolean;
}

/**
 * Stands in for a detail page while it loads.
 *
 * These pages used to show a single line of grey text ("Loading..."), which
 * said nothing about what was coming and then jumped straight to a full page
 * of content. This traces the shape that is about to appear — header, summary
 * cards, tab strip, body — so the page settles into place instead of replacing
 * itself. The knobs exist because the pages differ in how much they carry, not
 * so callers can invent new layouts.
 */
export function DetailPageSkeleton({
  label,
  cards = 4,
  tabs = 6,
  lines = 8,
  titledBody = false,
}: DetailPageSkeletonProps) {
  // Ragged, so the body reads as prose rather than a block.
  const width = (i: number) => `${[100, 96, 98, 72, 90, 94, 60, 86][i % 8]}%`;

  return (
    <div
      className="flex flex-col gap-4 p-4 md:p-6"
      role="status"
      aria-label={label}
    >
      {/* HEADER — back button, title and the status chips on the right. */}
      <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-4 shadow-lifted">
        <div className="flex flex-wrap items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-lg" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-5 w-56 max-w-full" />
            <Skeleton className="h-3 w-80 max-w-full" />
          </div>
          <Skeleton className="h-7 w-32 rounded-full" />
          <Skeleton className="h-7 w-24 rounded-full" />
        </div>
      </div>

      {cards > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: cards }).map((_, i) => (
            <div
              key={i}
              className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-4 shadow-lifted"
            >
              <Skeleton className="h-3 w-20" />
              <div className="mt-3 flex items-center gap-2">
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-28" />
              </div>
            </div>
          ))}
        </div>
      )}

      {tabs > 0 && (
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: tabs }).map((_, i) => (
            <Skeleton
              key={i}
              className="h-9 rounded-lg"
              style={{ width: [64, 44, 84, 60, 76, 120, 130, 88][i % 8] }}
            />
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-5 shadow-lifted">
        {titledBody && <Skeleton className="mx-auto h-5 w-52" />}
        <div className={titledBody ? "mt-6 space-y-3" : "space-y-3"}>
          {Array.from({ length: lines }).map((_, i) => (
            <Skeleton key={i} className="h-3" style={{ width: width(i) }} />
          ))}
        </div>
      </div>
    </div>
  );
}
